import { Navigate, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useFeatureFlags } from '../context/FeatureFlagsContext';
import {
  ArrowRight,
  Brain,
  CalendarPlus,
  ExternalLink,
  FileText,
  Gamepad2,
  Heart,
  Stethoscope,
  Users,
  Video,
} from 'lucide-react';

// Paleta INTUS "Electric Mint" (Brief_INTUS): cálida, clara, poco intimidante.
const INTUS_PRIMARY = '#00BFA6';
const INTUS_ON_PRIMARY = '#003B34';
const INTUS_PRIMARY_CONTAINER = '#B8F5EC';
const INTUS_SECONDARY_CONTAINER = '#B8F3FF';
const INTUS_ON_SECONDARY_CONTAINER = '#005566';
const INTUS_BACKGROUND = '#F1FFFC';
const INTUS_ON_SURFACE = '#202A2A';
const INTUS_ON_SURFACE_VARIANT = '#657373';
const INTUS_OUTLINE = '#D7E5E2';

/** Actividades de Vita360 App (mismos iconos/rutas del MainLayout), filtradas por feature flags. */
const activities: { key: string; name: string; desc: string; href: string; icon: typeof Brain }[] = [
  { key: 'evaluaciones', name: 'Evaluaciones', desc: 'Cuestionarios de bienestar', href: '/evaluacion', icon: Brain },
  { key: 'ejercicios_juegos', name: 'Ejercicios', desc: 'Hábitos que te hacen bien', href: '/ejercicios', icon: Heart },
  { key: 'videos', name: 'Videos', desc: 'Contenido para ti', href: '/videos', icon: Video },
  { key: 'comunidad', name: 'Comunidad', desc: 'Acompañamiento y foros', href: '/comunidad', icon: Users },
  { key: 'ejercicios_juegos', name: 'Juegos', desc: 'Aprende jugando', href: '/juegos', icon: Gamepad2 },
];

/** "Tu proceso en Vita360": enlaces al portal paciente de Vita360 (F4). */
const intusLinks: { name: string; path: string; icon: typeof CalendarPlus }[] = [
  { name: 'Agendar o ver mi próxima cita', path: '/patient/appointments', icon: CalendarPlus },
  { name: 'Ver documentos compartidos', path: '/patient/documents', icon: FileText },
  { name: 'Historia clínica', path: '/patient/medical-record', icon: Stethoscope },
];

function WelcomeSkeleton() {
  return (
    <div className="flex h-dvh flex-col" style={{ backgroundColor: INTUS_BACKGROUND }} data-testid="welcome-skeleton">
      <div className="flex h-14 items-center px-4" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="h-8 w-28 animate-pulse rounded-lg" style={{ backgroundColor: INTUS_OUTLINE }} />
      </div>
      <div className="mx-auto w-full max-w-md space-y-5 px-4 py-6">
        <div className="space-y-2">
          <div className="h-7 w-48 animate-pulse rounded-lg" style={{ backgroundColor: INTUS_OUTLINE }} />
          <div className="h-4 w-64 animate-pulse rounded" style={{ backgroundColor: INTUS_PRIMARY_CONTAINER }} />
        </div>
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-16 animate-pulse rounded-2xl" style={{ backgroundColor: INTUS_PRIMARY_CONTAINER }} />
        ))}
      </div>
    </div>
  );
}

/**
 * Página de bienvenida a Vita360 App (identidad Vita360, mobile-first).
 * Pantalla completa SIN MainLayout, con apariencia de app nativa:
 * header fijo con safe-area-top, contenido con scroll propio y CTA
 * sticky siempre visible con safe-area-bottom.
 */
export default function WelcomePage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading, intusBaseUrl } = useAuthStore();
  const { isEnabled } = useFeatureFlags();

  if (isLoading) {
    return <WelcomeSkeleton />;
  }

  if (!isAuthenticated || !user) {
    // Sin sesión: volver al portal Vita360 (el login admin es secreto y el
    // flujo del paciente entra SOLO vía SSO). Nunca exponer /admin/login.
    return <Navigate to="/" replace />;
  }

  const firstName = user.nombre?.trim().split(/\s+/)[0] || user.nombre;
  const visibleActivities = activities.filter((a) => isEnabled(a.key));

  return (
    <div className="flex h-dvh flex-col" style={{ backgroundColor: INTUS_BACKGROUND }}>
      {/* Header fijo (status-bar aware) */}
      <header
        className="sticky top-0 z-10 border-b bg-white/90 backdrop-blur"
        style={{ borderColor: INTUS_OUTLINE, paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="mx-auto flex w-full max-w-md items-center justify-between gap-3 px-4 py-3">
          <img src="/logo_vita360.png" alt="Vita360" className="h-8 w-auto" />
          <span
            className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold"
            style={{ backgroundColor: INTUS_PRIMARY_CONTAINER, color: INTUS_ON_PRIMARY }}
          >
            Vita360 App
          </span>
        </div>
      </header>

      {/* Contenido con scroll propio (una columna, tarjetas apiladas) */}
      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-md space-y-6 px-4 py-6">
          {/* Saludo */}
          <section>
            <h1 className="text-2xl font-bold" style={{ color: INTUS_ON_SURFACE }}>
              Hola, {firstName} 👋
            </h1>
            <p className="mt-1 text-base" style={{ color: INTUS_ON_SURFACE_VARIANT }}>
              Tu espacio de bienestar dentro de Vita360
            </p>
          </section>

          {/* Qué puedes hacer aquí */}
          <section>
            <h2 className="mb-3 text-base font-semibold" style={{ color: INTUS_ON_SURFACE }}>
              Qué puedes hacer aquí
            </h2>
            <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
              {visibleActivities.map((a) => (
                <button
                  key={a.name}
                  type="button"
                  onClick={() => navigate(a.href)}
                  className="flex min-h-14 items-center gap-3 rounded-2xl bg-white p-3 text-left shadow-sm ring-1 ring-[#D7E5E2] transition hover:brightness-[1.02] active:scale-[0.99]"
                >
                  <div
                    className="flex size-10 shrink-0 items-center justify-center rounded-xl"
                    style={{ backgroundColor: INTUS_PRIMARY_CONTAINER, color: INTUS_PRIMARY }}
                  >
                    <a.icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold" style={{ color: INTUS_ON_SURFACE }}>
                      {a.name}
                    </p>
                    <p className="truncate text-xs" style={{ color: INTUS_ON_SURFACE_VARIANT }}>
                      {a.desc}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </section>

          {/* Tu proceso en Vita360 (solo si el backend entregó la base URL) */}
          {intusBaseUrl && (
            <section>
              <h2 className="mb-3 text-base font-semibold" style={{ color: INTUS_ON_SURFACE }}>
                Tu proceso en Vita360
              </h2>
              <div className="space-y-2.5">
                {intusLinks.map((l) => (
                  <a
                    key={l.path}
                    href={`${intusBaseUrl}${l.path}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex min-h-14 items-center gap-3 rounded-2xl bg-white p-3 shadow-sm ring-1 ring-[#D7E5E2] transition hover:brightness-[1.02]"
                  >
                    <div
                      className="flex size-10 shrink-0 items-center justify-center rounded-xl"
                      style={{ backgroundColor: INTUS_SECONDARY_CONTAINER, color: INTUS_ON_SECONDARY_CONTAINER }}
                    >
                      <l.icon className="h-5 w-5" />
                    </div>
                    <span className="min-w-0 flex-1 truncate text-sm font-medium" style={{ color: INTUS_ON_SURFACE }}>
                      {l.name}
                    </span>
                    <ExternalLink className="h-4 w-4 shrink-0" style={{ color: INTUS_ON_SURFACE_VARIANT }} />
                  </a>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      {/* Bottom CTA sticky: siempre visible sin scroll (safe-area-bottom) */}
      <div
        className="sticky bottom-0 z-10 border-t bg-white/95 backdrop-blur"
        style={{ borderColor: INTUS_OUTLINE, paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="mx-auto flex w-full max-w-md flex-col gap-2 px-4 py-3 sm:flex-row">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="inline-flex min-h-12 flex-1 items-center justify-center gap-2 rounded-full px-6 text-base font-semibold text-white shadow-md transition hover:brightness-105 active:scale-[0.99]"
            style={{ background: `linear-gradient(90deg, ${INTUS_PRIMARY}, #00D4FF)` }}
          >
            Comenzar
            <ArrowRight className="h-5 w-5" />
          </button>
          {intusBaseUrl && (
            <a
              href={`${intusBaseUrl}/patient/dashboard`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full border px-6 text-sm font-medium transition hover:bg-white active:scale-[0.99]"
              style={{ borderColor: INTUS_PRIMARY, color: INTUS_ON_PRIMARY }}
            >
              Ir a mi consulta en Vita360
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
